/**
 * Audio.ts
 * Sound and music management using Web Audio API with procedural generation
 * Provides retro-style music and sound effects without requiring audio files
 */

import { ProceduralAudio } from './ProceduralAudio';
import { AudioTrack, SoundEffect } from './data/AudioData';

export interface AudioConfig {
  muted?: boolean;
  masterVolume?: number;
  musicVolume?: number;
  sfxVolume?: number;
}

interface FadeState {
  active: boolean;
  startVolume: number;
  targetVolume: number;
  duration: number;
  elapsed: number;
  onComplete?: () => void;
}

export class AudioManager {
  // Web Audio API context
  private audioContext: AudioContext;
  private proceduralAudio: ProceduralAudio;

  // Volume control
  private masterGain: GainNode;
  private musicGain: GainNode;
  private sfxGain: GainNode;

  // Volumes (0-1)
  private _masterVolume: number;
  private _musicVolume: number;
  private _sfxVolume: number;
  private _muted: boolean;

  // Current music state
  private currentTrack: AudioTrack | null = null;
  private musicNode: AudioBufferSourceNode | null = null;
  // private musicStartTime: number = 0; // TODO: Use for seeking/pause functionality
  // private musicPauseTime: number = 0; // TODO: Implement seeking when resuming
  private musicPaused: boolean = false;

  // Fade state
  private fadeState: FadeState | null = null;

  // Loaded audio files (for when we have actual files)
  private loadedSounds: Map<string, AudioBuffer> = new Map();

  constructor(config?: Partial<AudioConfig>) {
    // Initialize volumes
    this._masterVolume = config?.masterVolume ?? 0.7;
    this._musicVolume = config?.musicVolume ?? 0.6;
    this._sfxVolume = config?.sfxVolume ?? 0.5;
    this._muted = config?.muted ?? false;

    // Create audio context
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Create gain nodes for volume control
    this.masterGain = this.audioContext.createGain();
    this.musicGain = this.audioContext.createGain();
    this.sfxGain = this.audioContext.createGain();

    // Connect gain hierarchy: music/sfx -> master -> destination
    this.musicGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.masterGain.connect(this.audioContext.destination);

    // Apply initial volumes
    this.updateGainNodes();

    // Create procedural audio generator
    this.proceduralAudio = new ProceduralAudio(this.audioContext);
  }

  // =========================================================================
  // Volume Control
  // =========================================================================

  private updateGainNodes(): void {
    const masterVol = this._muted ? 0 : this._masterVolume;
    this.masterGain.gain.setValueAtTime(masterVol, this.audioContext.currentTime);
    this.musicGain.gain.setValueAtTime(this._musicVolume, this.audioContext.currentTime);
    this.sfxGain.gain.setValueAtTime(this._sfxVolume, this.audioContext.currentTime);
  }

  setMasterVolume(volume: number): void {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    this.updateGainNodes();
  }

  setMusicVolume(volume: number): void {
    this._musicVolume = Math.max(0, Math.min(1, volume));
    this.updateGainNodes();
  }

  setSFXVolume(volume: number): void {
    this._sfxVolume = Math.max(0, Math.min(1, volume));
    this.updateGainNodes();
  }

  getMasterVolume(): number {
    return this._masterVolume;
  }

  getMusicVolume(): number {
    return this._musicVolume;
  }

  getSFXVolume(): number {
    return this._sfxVolume;
  }

  toggleMute(): void {
    this.setMuted(!this._muted);
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    this.updateGainNodes();
  }

  isMuted(): boolean {
    return this._muted;
  }

  // =========================================================================
  // Music Playback
  // =========================================================================

  /**
   * Play a music track with looping
   */
  async playMusic(track: AudioTrack, loop: boolean = true): Promise<void> {
    // Resume audio context if needed (for browser autoplay policies)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Don't restart if already playing this track
    if (this.currentTrack === track && this.isMusicPlaying()) {
      return;
    }

    // Stop current music
    this.stopMusic();

    // Generate procedural music
    const buffer = await this.proceduralAudio.generateMusic(track);
    if (!buffer) {
      console.warn(`Failed to generate music: ${track}`);
      return;
    }

    // Create and configure source node
    this.musicNode = this.audioContext.createBufferSource();
    this.musicNode.buffer = buffer;
    this.musicNode.loop = loop;
    this.musicNode.connect(this.musicGain);

    // Start playback
    // this.musicStartTime = this.audioContext.currentTime;
    // this.musicPauseTime = 0;
    this.musicPaused = false;
    this.currentTrack = track;
    this.musicNode.start(0);
  }

  /**
   * Stop music playback
   */
  stopMusic(): void {
    if (this.musicNode) {
      try {
        this.musicNode.stop();
      } catch (e) {
        // Already stopped
      }
      this.musicNode.disconnect();
      this.musicNode = null;
    }
    this.currentTrack = null;
    this.musicPaused = false;
    // this.musicStartTime = 0;
    // this.musicPauseTime = 0;
    this.fadeState = null;
  }

  /**
   * Pause music playback
   */
  pauseMusic(): void {
    if (this.musicNode && !this.musicPaused) {
      // this.musicPauseTime = this.audioContext.currentTime - this.musicStartTime;
      this.stopMusic();
      this.musicPaused = true;
    }
  }

  /**
   * Resume paused music
   */
  async resumeMusic(): Promise<void> {
    if (this.musicPaused && this.currentTrack) {
      const track = this.currentTrack;
      // const pauseTime = this.musicPauseTime; // TODO: Implement seeking
      this.musicPaused = false;
      await this.playMusic(track);
      // TODO: Seek to pause time if needed
    }
  }

  /**
   * Fade out current music and fade in new track
   */
  async fadeToMusic(
    track: AudioTrack,
    fadeOutDuration: number = 1000,
    fadeInDuration: number = 1000
  ): Promise<void> {
    if (this.currentTrack) {
      // Fade out current track
      await this.fadeOut(fadeOutDuration);
    }

    // Play new track
    await this.playMusic(track);

    // Fade in new track
    await this.fadeIn(fadeInDuration);
  }

  /**
   * Fade music in
   */
  async fadeIn(duration: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      const startVolume = 0;
      const targetVolume = this._musicVolume;

      this.musicGain.gain.setValueAtTime(startVolume, this.audioContext.currentTime);
      this.fadeState = {
        active: true,
        startVolume,
        targetVolume,
        duration,
        elapsed: 0,
        onComplete: resolve,
      };

      this.updateFade();
    });
  }

  /**
   * Fade music out
   */
  async fadeOut(duration: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      const startVolume = this._musicVolume;
      const targetVolume = 0;

      this.fadeState = {
        active: true,
        startVolume,
        targetVolume,
        duration,
        elapsed: 0,
        onComplete: () => {
          this.stopMusic();
          resolve();
        },
      };

      this.updateFade();
    });
  }

  private updateFade(): void {
    if (!this.fadeState || !this.fadeState.active) return;

    const progress = Math.min(1, this.fadeState.elapsed / this.fadeState.duration);
    const volume =
      this.fadeState.startVolume +
      (this.fadeState.targetVolume - this.fadeState.startVolume) * progress;

    this.musicGain.gain.setValueAtTime(volume, this.audioContext.currentTime);

    if (progress >= 1) {
      this.fadeState.active = false;
      if (this.fadeState.onComplete) {
        this.fadeState.onComplete();
      }
      this.fadeState = null;
    } else {
      this.fadeState.elapsed += 16; // Assume ~60fps
      requestAnimationFrame(() => this.updateFade());
    }
  }

  /**
   * Check if music is currently playing
   */
  isMusicPlaying(): boolean {
    return this.musicNode !== null && !this.musicPaused;
  }

  /**
   * Get current music track
   */
  getCurrentTrack(): AudioTrack | null {
    return this.currentTrack;
  }

  // =========================================================================
  // Sound Effects
  // =========================================================================

  /**
   * Play a sound effect
   */
  async playSFX(effect: SoundEffect): Promise<void> {
    // Resume audio context if needed
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    if (this._muted) return;

    // Generate procedural sound effect
    const buffer = await this.proceduralAudio.generateSFX(effect);
    if (!buffer) {
      console.warn(`Failed to generate SFX: ${effect}`);
      return;
    }

    // Create and play source node
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.sfxGain);
    source.start(0);

    // Auto-cleanup
    source.onended = () => {
      source.disconnect();
    };
  }

  // =========================================================================
  // File Loading (for future use with actual audio files)
  // =========================================================================

  /**
   * Load an audio file
   */
  async loadAudioFile(name: string, url: string): Promise<void> {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.loadedSounds.set(name, audioBuffer);
    } catch (error) {
      console.error(`Failed to load audio file: ${url}`, error);
      throw error;
    }
  }

  /**
   * Play a loaded audio file
   */
  playLoadedSound(name: string, destination: 'music' | 'sfx' = 'sfx'): void {
    const buffer = this.loadedSounds.get(name);
    if (!buffer) {
      console.warn(`Audio file not loaded: ${name}`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(destination === 'music' ? this.musicGain : this.sfxGain);
    source.start(0);

    source.onended = () => {
      source.disconnect();
    };
  }

  // =========================================================================
  // Cleanup
  // =========================================================================

  /**
   * Clean up audio resources
   */
  dispose(): void {
    this.stopMusic();
    this.loadedSounds.clear();

    if (this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}
